<template>
  <li :class="['sam-icon-list-item', { 'is-disabled': item.disabled }]">
    <div
      class="item-content"
      tabindex="-1"
      v-on="item.disabled ? {} : $listeners"
    >
      <div :class="`item-icon-frame is-${frameStyle}`">
        <SamIcon
          :icon="item.icon"
          class="item-icon"
        />
      </div>
      <span class="item-label">{{ item.label }}</span>
    </div>
  </li>
</template>

<script>
import SAMORA_STYLES from '../../../utils/constants/samora-styles.enum';
import SamIcon from '../SamIcon/SamIcon.vue';

/**
 * The SamIconListItem object structure.
 * @typedef {Object} SamIconListItem
 * @property {string} label - The item label.
 * @property {string} icon - The icon to display.
 * @property {any} value - Adds disabled state to item.
 * @property {boolean} [disabled] - Adds disabled state to item.
 */

export const FRAME_BACKGROUND_STYLE = {
  DEFAULT: SAMORA_STYLES.DEFAULT, // secondary baby blue
  INTERNAL: SAMORA_STYLES.SECONDARY, // secondary linen
};

export default {
  name: 'SamIconListItem',
  components: { SamIcon },
  props: {
    /**
     * The item to be shown. It must follow a specific structure.
     * @type {SamIconListItem}
     */
    item: {
      type: Object,
      required: true,
      default: () => ({
        label: '',
        icon: '',
        disabled: false,
        value: null,
      }),
    },
    /**
     * The style to be applied to the icon frame
     */
    frameStyle: {
      type: String,
      required: false,
      default: FRAME_BACKGROUND_STYLE.DEFAULT,
      validator: (val) => Object.values(FRAME_BACKGROUND_STYLE).includes(val),
    },
  },
};
</script>

<style scoped lang="scss">
.sam-icon-list-item {
  @extend .d-flex, .justify-center;

  .item-content {
    @extend .p-2, .d-flex, .flex-column, .start-xs, .items-center;
    cursor: pointer;
    width: $size--16;
    text-align: center;
    border: $size--0--fourth solid transparent;
    border-radius: $size--1;

    .item-icon-frame {
      width: $size--12;
      height: $size--12;
      border-radius: $size--12;
      display: table;
      background-color: $background--baby-blue;

      .item-icon {
        ::v-deep(path) {
          fill: $neutral--90;
        }
        display: table-cell;
        vertical-align: middle;
      }

      &.is-secondary {
        background-color: $background--linen;
      }
    }

    .item-label {
      @extend .text--caption, .mt-2;
    }

    &:hover {
      border-color: $informative--hover;
      background-color: $informative--surface;

      .item-label {
        color: $informative--hover;
      }
    }

    &:focus {
      border-color: $informative--hover;
      background-color: $informative--surface;
      outline: 5px solid $informative--border;

      .item-label {
        color: $informative--hover;
      }
    }
  }

  &.is-disabled {
    .item-content {
      pointer-events: none;

      .item-label {
        color: $neutral--60;
      }

      .item-icon-frame {
        background: none;

        .item-icon ::v-deep(path) {
          fill: $neutral--60;
        }
      }
    }
  }
}
</style>
